import { useApiClient } from "./api-client";

export interface ExtractDocumentResponse {
  ok: boolean;
  data?: unknown;
  request_id: string;
  saved_path?: string;
  error?: string;
  raw_text?: string;
}

/**
 * Response from the precheck endpoint.
 * Contains validation results for Forge operations.
 */
export interface PrecheckResponse {
  /** Whether all checks passed */
  valid: boolean;
  /** Human-readable status message */
  message: string;
  /** Individual check results */
  checks: Record<string, boolean>;
  /** List of failed requirements (only present when valid is false) */
  missing_requirements?: string[];
  /** Microsoft Entra tenant id resolved from the Clerk-linked Microsoft account */
  entra_tenant_id?: string | null;
  /** Whether the user may choose between available AI providers */
  can_select_ai_provider?: boolean;
  /** Provider id that must be used when provider selection is restricted */
  forced_ai_provider?: string | null;
}

export interface ProviderResponse {
  providers: Array<{
    id: string;
    name: string;
    supports_images: boolean;
    description: string;
    platform: string;
    region: string | null;
    dev_only: boolean;
    group: string | null;
  }>;
  entra_tenant_id?: string | null;
  can_select_ai_provider?: boolean;
  forced_ai_provider?: string | null;
}

export const useApiActions = () => {
  const { apiClient, authenticatedRequest, refreshToken } = useApiClient();

  // Document extraction actions
  const extractDocument = async (
    formData: FormData
  ): Promise<ExtractDocumentResponse> => {
    return authenticatedRequest(async (token) => {
      return apiClient.postForm<ExtractDocumentResponse>(
        "/extract/document",
        formData,
        token
      );
    });
  };

  // Document conversion actions
  const convertJsonRaw = async (
    data: unknown,
    label?: string
  ): Promise<Response> => {
    return authenticatedRequest(async (token) => {
      const payload = {
        data,
        label: label || "json-data",
      };
      return apiClient.postRaw("/convert/json", payload, token);
    });
  };

  // Precheck actions
  /**
   * Validates user permissions for Forge operations.
   * Should be called before enabling the submit button.
   * If the first attempt fails with an authentication error, it will
   * refresh the token and retry once.
   *
   * @returns Precheck result containing validation status and any missing requirements
   */
  const precheck = async (): Promise<PrecheckResponse> => {
    try {
      return await authenticatedRequest(async (token) => {
        return apiClient.get<PrecheckResponse>("/precheck", token);
      });
    } catch (error: unknown) {
      // Check if the error is an authentication failure
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      const isAuthError =
        errorMessage.includes("invalid or expired token") ||
        errorMessage.includes("Authentication failed") ||
        errorMessage.includes("401");

      if (isAuthError) {
        // Refresh the token and retry once
        const freshToken = await refreshToken();
        return apiClient.get<PrecheckResponse>("/precheck", freshToken);
      }

      // Re-throw non-auth errors
      throw error;
    }
  };

  const getProviders = async (): Promise<ProviderResponse> => {
    return authenticatedRequest(async (token) => {
      return apiClient.get<ProviderResponse>("/providers", token);
    });
  };

  return {
    extractDocument,
    convertJsonRaw,
    precheck,
    getProviders,
  };
};
